import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Plus, Minus, Trash2, ShoppingBag, Tag, Truck, FileText, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Checkout — informational e-commerce checkout that terminates in an invoice request.
 * No payment. All pricing/coupon/shipping evaluation happens on the backend via /api/checkout/quote.
 */
export default function Checkout() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { cart, updateQty, removeItem, refresh } = useCart();

  const [addons, setAddons] = useState([]);
  const [shippingRates, setShippingRates] = useState([]);
  const [shippingCode, setShippingCode] = useState("");
  const [couponInput, setCouponInput] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [couponError, setCouponError] = useState("");
  const [quote, setQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [address, setAddress] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    company: user?.business_name || "",
    line1: "", line2: "", city: "", state: "", postal_code: "", country: "US",
    phone: user?.phone || "",
    email: user?.email || "",
  });
  const [notes, setNotes] = useState("");
  const [agreed, setAgreed] = useState(false);

  // Load defaults + addons + shipping rates + primary address
  useEffect(() => {
    refresh();
    api.get("/checkout/addons").then((r) => setAddons(r.data || [])).catch(() => {});
    api.get("/checkout/shipping-rates").then((r) => {
      setShippingRates(r.data || []);
      const def = (r.data || [])[0];
      if (def && !shippingCode) setShippingCode(def.code);
    }).catch(() => {});
    api.get("/addresses").then((r) => {
      const def = (r.data || []).find((a) => a.is_default) || (r.data || [])[0];
      if (def) {
        setAddress((a) => ({
          ...a,
          first_name: def.full_name?.split(" ")[0] || a.first_name,
          last_name: def.full_name?.split(" ").slice(1).join(" ") || a.last_name,
          line1: def.line1 || "",
          line2: def.line2 || "",
          city: def.city || "",
          state: def.state || "",
          postal_code: def.postal_code || "",
          country: def.country || "US",
          phone: def.phone || a.phone,
        }));
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recompute quote whenever cart, coupon or shipping changes
  useEffect(() => {
    if (!cart || cart.items?.length === 0) { setQuote(null); return; }
    api.post("/checkout/quote", { coupon_code: couponCode, shipping_code: shippingCode })
      .then((r) => { setQuote(r.data); setCouponError(r.data.coupon_error || ""); })
      .catch(() => {});
  }, [cart, couponCode, shippingCode]);

  const applyCoupon = async () => {
    const code = (couponInput || "").trim().toUpperCase();
    setCouponCode(code);
    if (!code) setCouponError("");
  };

  const addAddon = async (addon) => {
    const def = (addon.variants || []).find((v) => v.default && v.active !== false) || (addon.variants || [])[0];
    if (!def) return;
    await api.post("/cart/items", { peptide_slug: addon.slug, variant_id: def.id, quantity: 1 });
    toast.success(`Added ${addon.name}`);
    refresh();
  };

  const submit = async () => {
    if (!agreed) { toast.error("Please accept the research agreement"); return; }
    if (!cart?.items?.length) { toast.error("Your cart is empty"); return; }
    for (const k of ["first_name", "last_name", "line1", "city", "state", "postal_code", "phone", "email"]) {
      if (!(address[k] || "").trim()) { toast.error(`Missing shipping.${k}`); return; }
    }
    setSubmitting(true);
    try {
      const r = await api.post("/checkout/submit", {
        shipping: address,
        coupon_code: couponCode || "",
        shipping_code: shippingCode || "",
        notes,
        research_agreement: true,
      });
      toast.success("Order submitted");
      refresh();
      nav(`/order-success/${r.data.order_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  const inCartSlugs = useMemo(() => new Set((cart?.items || []).map((i) => i.peptide_slug)), [cart]);
  const filteredAddons = addons.filter((a) => !inCartSlugs.has(a.slug));

  if (!cart || cart.items?.length === 0) {
    return (
      <div data-testid="checkout-empty" className="max-w-3xl mx-auto px-6 py-24 text-center">
        <ShoppingBag className="w-10 h-10 mx-auto text-slate-300" strokeWidth={1.25}/>
        <h1 className="mt-6 font-display text-3xl">Your cart is empty.</h1>
        <p className="mt-2 text-slate-600">Add products to your cart before starting checkout.</p>
        <Button onClick={() => nav("/catalog")} className="mt-6 bg-blue-600 hover:bg-blue-700 text-white">Browse catalog</Button>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="eyebrow mb-2">Checkout</div>
      <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Submit your research order.</h1>
      <p className="mt-2 text-slate-600 text-sm max-w-2xl">
        No payment now — we'll review your request and email an invoice with payment instructions.
      </p>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-8">
        {/* LEFT column */}
        <div className="space-y-6">
          {/* Order Summary */}
          <section className="border border-slate-200 rounded-md bg-white">
            <header className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <ShoppingBag className="w-4 h-4 text-slate-700" strokeWidth={1.75}/>
              <div className="font-display text-lg font-semibold">Order summary</div>
              <Badge className="rounded-md font-mono text-[10px] uppercase tracking-widest bg-slate-100 text-slate-600 border border-slate-200">{cart.count} items</Badge>
            </header>
            <div className="divide-y divide-slate-100" data-testid="checkout-lines">
              {cart.items.map((it) => (
                <div key={it.line_id} data-testid={`checkout-line-${it.line_id}`} className="p-5 flex gap-4">
                  <div className="w-20 h-20 rounded-md border border-slate-200 bg-slate-50 overflow-hidden flex-shrink-0">
                    {it.image_url ? <img src={it.image_url} alt="" className="w-full h-full object-cover"/> : null}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-display text-sm font-semibold text-slate-900">{it.name}</div>
                        {it.variant_label && <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">{it.variant_label}</div>}
                      </div>
                      <button onClick={() => removeItem(it.line_id)} className="p-1 rounded hover:bg-red-50 text-red-500" aria-label="Remove"><Trash2 className="w-3.5 h-3.5" strokeWidth={1.75}/></button>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center border border-slate-200 rounded-md">
                        <button onClick={() => updateQty(it.line_id, Math.max(0, it.quantity - 1))} className="p-1.5 hover:bg-slate-50"><Minus className="w-3 h-3" strokeWidth={1.75}/></button>
                        <div className="px-3 text-sm font-mono w-8 text-center">{it.quantity}</div>
                        <button onClick={() => updateQty(it.line_id, it.quantity + 1)} className="p-1.5 hover:bg-slate-50"><Plus className="w-3 h-3" strokeWidth={1.75}/></button>
                      </div>
                      <div className="text-right">
                        <div className="font-mono text-[11px] text-slate-500">${Number(it.unit_price).toFixed(2)} × {it.quantity}</div>
                        <div className="font-mono text-sm font-semibold text-slate-900">${Number(it.line_total).toFixed(2)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Add-ons */}
          {filteredAddons.length > 0 && (
            <section className="border border-slate-200 rounded-md bg-white">
              <header className="px-5 py-4 border-b border-slate-100">
                <div className="font-display text-lg font-semibold">Recommended add-ons</div>
                <p className="text-xs text-slate-500 mt-0.5">Frequently requested supplies for your research.</p>
              </header>
              <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="checkout-addons">
                {filteredAddons.slice(0, 4).map((a) => {
                  const def = (a.variants || []).find((v) => v.default && v.active !== false) || (a.variants || [])[0] || {};
                  return (
                    <div key={a.slug} data-testid={`addon-${a.slug}`} className="border border-slate-200 rounded-md p-3 bg-slate-50/50 flex flex-col">
                      <div className="w-full aspect-square rounded-md border border-slate-200 bg-white overflow-hidden mb-2">
                        {a.image_url ? <img src={a.image_url} alt="" className="w-full h-full object-cover"/> : null}
                      </div>
                      <div className="font-display text-xs font-semibold text-slate-900 line-clamp-2">{a.name}</div>
                      <div className="mt-1 font-mono text-sm text-slate-900">${Number(def.price || 0).toFixed(2)}</div>
                      <Button data-testid={`addon-add-${a.slug}`} onClick={() => addAddon(a)} className="mt-3 h-8 text-xs bg-slate-900 hover:bg-blue-600 text-white">+ Add</Button>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Shipping */}
          <section className="border border-slate-200 rounded-md bg-white">
            <header className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <Truck className="w-4 h-4 text-slate-700" strokeWidth={1.75}/>
              <div className="font-display text-lg font-semibold">Shipping information</div>
            </header>
            <div className="p-5 grid grid-cols-2 gap-3">
              <Input required data-testid="ship-first-name" placeholder="First name" value={address.first_name} onChange={(e) => setAddress({...address, first_name: e.target.value})}/>
              <Input required data-testid="ship-last-name" placeholder="Last name" value={address.last_name} onChange={(e) => setAddress({...address, last_name: e.target.value})}/>
              <Input placeholder="Company (optional)" className="col-span-2" value={address.company} onChange={(e) => setAddress({...address, company: e.target.value})}/>
              <Input required data-testid="ship-line1" placeholder="Address line 1" className="col-span-2" value={address.line1} onChange={(e) => setAddress({...address, line1: e.target.value})}/>
              <Input placeholder="Address line 2 (optional)" className="col-span-2" value={address.line2} onChange={(e) => setAddress({...address, line2: e.target.value})}/>
              <Input required data-testid="ship-city" placeholder="City" value={address.city} onChange={(e) => setAddress({...address, city: e.target.value})}/>
              <Input required data-testid="ship-state" placeholder="State/Province" value={address.state} onChange={(e) => setAddress({...address, state: e.target.value})}/>
              <Input required data-testid="ship-postal" placeholder="Postal code" value={address.postal_code} onChange={(e) => setAddress({...address, postal_code: e.target.value})}/>
              <Input required data-testid="ship-country" placeholder="Country" value={address.country} onChange={(e) => setAddress({...address, country: e.target.value})}/>
              <Input required data-testid="ship-phone" placeholder="Phone" value={address.phone} onChange={(e) => setAddress({...address, phone: e.target.value})}/>
              <Input required type="email" data-testid="ship-email" placeholder="Email" value={address.email} onChange={(e) => setAddress({...address, email: e.target.value})}/>
            </div>
          </section>

          {/* Notes */}
          <section className="border border-slate-200 rounded-md bg-white">
            <header className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <FileText className="w-4 h-4 text-slate-700" strokeWidth={1.75}/>
              <div className="font-display text-lg font-semibold">Order notes</div>
              <span className="text-xs text-slate-500">Optional</span>
            </header>
            <div className="p-5">
              <Textarea data-testid="checkout-notes" rows={4} placeholder="Anything our fulfillment team should know?" value={notes} onChange={(e) => setNotes(e.target.value)}/>
            </div>
          </section>

          {/* Research Agreement */}
          <section className="border border-slate-200 rounded-md bg-blue-50/40">
            <div className="p-5 flex items-start gap-3">
              <input id="research-agree" data-testid="research-agree" type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1 h-4 w-4 text-blue-600"/>
              <label htmlFor="research-agree" className="text-sm text-slate-700 cursor-pointer">
                <div className="font-medium text-slate-900">Research use only agreement</div>
                <div className="text-xs text-slate-600 mt-1">I confirm these products will be used solely for veterinary and laboratory research. Not for human consumption. Not evaluated by the FDA.</div>
              </label>
            </div>
          </section>
        </div>

        {/* RIGHT column — totals + submit */}
        <aside>
          <div className="lg:sticky lg:top-24 space-y-4">
            <section className="border border-slate-200 rounded-md bg-white p-5">
              <div className="flex items-center gap-2 mb-3">
                <Tag className="w-4 h-4 text-slate-700" strokeWidth={1.75}/>
                <div className="font-display text-base font-semibold">Discount code</div>
              </div>
              <div className="flex gap-2">
                <Input data-testid="coupon-input" placeholder="Enter code" value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), applyCoupon())}/>
                <Button data-testid="coupon-apply-btn" type="button" onClick={applyCoupon} className="bg-slate-900 hover:bg-blue-600 text-white h-10">Apply</Button>
              </div>
              {couponCode && !couponError && <div className="mt-2 text-xs text-blue-700 font-mono flex items-center gap-1"><Check className="w-3 h-3"/> {couponCode} applied · −${(quote?.discount || 0).toFixed(2)}</div>}
              {couponError && <div className="mt-2 text-xs text-red-600" data-testid="coupon-error">{couponError}</div>}
            </section>

            <section className="border border-slate-200 rounded-md bg-white p-5">
              <div className="flex items-center gap-2 mb-3">
                <Truck className="w-4 h-4 text-slate-700" strokeWidth={1.75}/>
                <div className="font-display text-base font-semibold">Shipping method</div>
              </div>
              <div className="space-y-2" role="radiogroup" data-testid="shipping-options">
                {shippingRates.map((r) => (
                  <label key={r.code} data-testid={`shipping-${r.code}`} className={`flex items-start gap-3 border rounded-md p-3 cursor-pointer transition ${shippingCode === r.code ? "border-blue-500 bg-blue-50/50" : "border-slate-200 hover:border-slate-400"}`}>
                    <input type="radio" name="shipping" checked={shippingCode === r.code} onChange={() => setShippingCode(r.code)} className="mt-1"/>
                    <div className="flex-1">
                      <div className="flex justify-between items-baseline">
                        <div className="font-medium text-sm text-slate-900">{r.label}</div>
                        <div className="font-mono text-sm">{Number(r.cost) === 0 ? "Free" : `$${Number(r.cost).toFixed(2)}`}</div>
                      </div>
                      <div className="text-xs text-slate-500">{r.description}</div>
                      {r.free_over > 0 && r.cost > 0 && <div className="text-[10px] font-mono text-blue-700 mt-0.5">FREE ON ORDERS OVER ${Number(r.free_over).toFixed(0)}</div>}
                    </div>
                  </label>
                ))}
              </div>
            </section>

            <section className="border border-slate-200 rounded-md bg-white p-5">
              <div className="font-display text-base font-semibold mb-3">Totals</div>
              <div className="space-y-1.5 text-sm">
                <Row label="Subtotal" value={`$${(quote?.subtotal ?? cart.subtotal).toFixed(2)}`}/>
                {(quote?.discount || 0) > 0 && <Row label={`Discount${quote?.coupon_code ? ` (${quote.coupon_code})` : ""}`} value={`−$${quote.discount.toFixed(2)}`} negative/>}
                <Row label={`Shipping${quote?.shipping_label ? ` · ${quote.shipping_label}` : ""}`} value={`$${(quote?.shipping_cost ?? 0).toFixed(2)}`}/>
                <div className="border-t border-slate-100 mt-2 pt-3 flex items-baseline justify-between">
                  <div className="font-display text-base font-semibold">Grand total</div>
                  <div data-testid="checkout-total" className="font-mono text-lg font-semibold">${(quote?.total ?? cart.subtotal).toFixed(2)}</div>
                </div>
              </div>
            </section>

            <Button data-testid="submit-order-btn" onClick={submit} disabled={!agreed || submitting} className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-md">
              {submitting ? "Submitting…" : "Submit order request"}
            </Button>
            <p className="text-[11px] text-slate-500 text-center px-4">
              By submitting, you agree to receive an invoice by email. No card is charged today.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Row({ label, value, negative }) {
  return (
    <div className="flex items-baseline justify-between">
      <div className="text-slate-600">{label}</div>
      <div className={`font-mono ${negative ? "text-blue-700" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}
